import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FileSelection } from '@/components/desktop-side-panel/file-selection-toolbar';
import type { OpenedWorkspaceFile } from '@/components/desktop-side-panel/files-view';
import { cloudWorkspaceFileUrl } from '@/lib/cloud-runtime/cloud-file-url';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import { errorMessage } from '@/lib/error-message';
import type { WorkspaceEntry } from '@/lib/native-desktop';
import { previewMediaType } from '@/lib/side-panel/file-explorer';
import { useSelectionDismiss } from '@/lib/side-panel/use-selection-dismiss';
import { nativeWorkspaceMediaUrl } from '@/lib/workspace-media-url';
import type { WorkspaceClient } from '@/lib/workspaces/workspace-client';

export function useWorkspaceFileExplorer({
  active,
  client,
  cloudRuntime,
  filesError,
  setFilesError,
  taskId,
  workspace,
}: {
  active: boolean;
  client: WorkspaceClient | null;
  cloudRuntime: CloudRuntime | null;
  filesError: string | null;
  setFilesError: React.Dispatch<React.SetStateAction<string | null>>;
  taskId: string;
  workspace: string | null;
}) {
  const [fileEntries, setFileEntries] = useState<Record<string, WorkspaceEntry[]>>({});
  const [expandedDirectories, setExpandedDirectories] = useState<Set<string>>(new Set());
  const [loadingDirectories, setLoadingDirectories] = useState<Set<string>>(new Set());
  const [fileFilter, setFileFilter] = useState('');
  const [activeFile, setActiveFile] = useState<OpenedWorkspaceFile | null>(null);
  const [fileSelection, setFileSelection] = useState<FileSelection | null>(null);
  const [showFileSource, setShowFileSource] = useState(false);
  const generation = useRef(0);
  const fileRequest = useRef(0);
  const directoryRequests = useRef(new Map<string, number>());
  const nextDirectoryRequest = useRef(0);
  const entriesRef = useRef(fileEntries);

  // biome-ignore lint/correctness/useExhaustiveDependencies(client): another client starts the explorer over.
  // biome-ignore lint/correctness/useExhaustiveDependencies(workspace): another workspace starts the explorer over.
  // biome-ignore lint/correctness/useExhaustiveDependencies(taskId): another task starts the explorer over.
  useLayoutEffect(() => {
    generation.current += 1;
    fileRequest.current += 1;
    directoryRequests.current.clear();
    entriesRef.current = {};
    setFileEntries({});
    setActiveFile(null);
    setFileSelection(null);
    setExpandedDirectories(new Set());
    setLoadingDirectories(new Set());
    setFileFilter('');
    setShowFileSource(false);
    setFilesError(null);
  }, [client, workspace, taskId, setFilesError]);

  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );

  const dismissFileSelection = useCallback(() => setFileSelection(null), []);
  useSelectionDismiss(fileSelection !== null, dismissFileSelection);

  const loadDirectory = useCallback(
    async (relativePath: string, force = false): Promise<void> => {
      if (client === null || workspace === null) return;
      if (!force && entriesRef.current[relativePath] !== undefined) return;
      const scope = generation.current;
      const request = ++nextDirectoryRequest.current;
      directoryRequests.current.set(relativePath, request);
      const current = () =>
        generation.current === scope && directoryRequests.current.get(relativePath) === request;
      setFilesError(null);
      setLoadingDirectories((current) => new Set(current).add(relativePath));
      try {
        const entries = await client.listDirectory(relativePath);
        if (!current()) return;
        entriesRef.current = { ...entriesRef.current, [relativePath]: entries };
        setFileEntries(entriesRef.current);
      } catch (error) {
        if (current()) setFilesError(errorMessage(error));
      } finally {
        if (current())
          setLoadingDirectories((current) => {
            const next = new Set(current);
            next.delete(relativePath);
            return next;
          });
      }
    },
    [client, workspace, setFilesError],
  );

  async function toggleDirectory(path: string): Promise<void> {
    const isExpanded = expandedDirectories.has(path);
    setExpandedDirectories((current) => {
      const next = new Set(current);
      if (isExpanded) next.delete(path);
      else next.add(path);
      return next;
    });
    if (!isExpanded) await loadDirectory(path);
  }

  async function refreshFiles(): Promise<void> {
    await Promise.all(['', ...expandedDirectories].map((path) => loadDirectory(path, true)));
  }

  async function openFile(path: string): Promise<void> {
    if (client === null || workspace === null) return;
    const scope = generation.current;
    const request = ++fileRequest.current;
    const current = () => generation.current === scope && fileRequest.current === request;
    setFilesError(null);
    setFileSelection(null);
    try {
      const mediaType = previewMediaType(path);
      let file: OpenedWorkspaceFile;
      if (mediaType !== null) {
        file = {
          path,
          kind: 'media',
          mediaType,
          url:
            cloudRuntime === null
              ? nativeWorkspaceMediaUrl(workspace, path)
              : cloudWorkspaceFileUrl(taskId, path),
        };
      } else {
        file = { path, kind: 'text', content: await client.readTextFile(path) };
      }
      if (!current()) return;
      setActiveFile(file);
      setShowFileSource(false);
    } catch (error) {
      if (current()) setFilesError(errorMessage(error));
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies(taskId): another task starts the explorer over (above): its root is read again.
  useEffect(() => {
    if (!active) return;
    void loadDirectory('', true);
  }, [active, loadDirectory, taskId]);

  return {
    activeFile,
    dismissFileSelection,
    expandedDirectories,
    fileEntries,
    fileFilter,
    fileSelection,
    filesError,
    loadingDirectories,
    openFile,
    refreshFiles,
    setFileFilter,
    setFileSelection,
    setFilesError,
    setShowFileSource,
    showFileSource,
    toggleDirectory,
  };
}
